import { useEffect, useState } from "react";
import { Keyboard, X } from "lucide-react";

// Global "?" shortcut help. Mounts a keyboard listener for `?` and
// opens a modal listing every shortcut the app exposes today. Pages
// can register additional shortcuts later by extending SHORTCUTS.
const SHORTCUTS = [
  { group: "Global", items: [
    { k: "?", l: "Show this keyboard shortcuts dialog" },
    { k: "/", l: "Focus the top-nav search" },
    { k: "Esc", l: "Close any open dialog / popover" },
  ]},
  { group: "Ball-by-ball scoring (BBP)", items: [
    { k: "0–6", l: "Score runs (legal delivery)" },
    { k: "W", l: "Wide" },
    { k: "N", l: "No-ball" },
    { k: "B", l: "Bye" },
    { k: "L", l: "Leg-bye" },
    { k: "K", l: "Mark wicket (then pick type in modal)" },
    { k: "U / Backspace", l: "Undo the last saved ball (30s window)" },
    { k: "Enter / Space", l: "Save the current ball" },
    { k: "V", l: "Toggle voice scoring on / off" },
  ]},
  { group: "Edit balls / QC", items: [
    { k: "Click row", l: "Expand QC row to show clip + actions" },
    { k: "Jump to 5.3", l: "Type over.ball in EditBalls and press Go" },
  ]},
  { group: "Forms (Add / Edit)", items: [
    { k: "Enter", l: "Advance to next step (or save on the last step)" },
    { k: "Drafts", l: "Auto-saved every 0.4s — Resume / Start fresh banner on reload" },
  ]},
];

export default function KeyboardHelp() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "?") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.target?.isContentEditable) return;
      e.preventDefault();
      setOpen((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div className="bg-bg-primary border border-border-default rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Keyboard className="w-4 h-4" /> Keyboard shortcuts
          </h3>
          <button onClick={() => setOpen(false)} className="text-text-secondary hover:text-text-primary p-1"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
          {SHORTCUTS.map((g) => (
            <div key={g.group}>
              <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-2">{g.group}</div>
              <ul className="space-y-1.5">
                {g.items.map((it) => (
                  <li key={it.k} className="flex items-baseline gap-3 text-xs">
                    <kbd className="font-mono px-1.5 py-0.5 rounded bg-bg-secondary border border-border-default text-text-primary shrink-0">{it.k}</kbd>
                    <span className="text-text-secondary">{it.l}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-5 text-[11px] text-text-secondary">
          Press <kbd className="font-mono px-1 py-0.5 rounded bg-bg-secondary border border-border-default">?</kbd> anywhere to toggle this dialog.
        </p>
      </div>
    </div>
  );
}
